import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Upload, X, Trash2, Link, ChevronDown } from 'lucide-react';
import DashboardLayout from '../layouts/DashboardLayout';
import Toast from '../components/Toast/Toast';
import Loading from '../components/Loading/Loading';
import ConfirmModal from '../components/ConfirmModal/ConfirmModal';
import { getStartupApi, saveStartupApi, deleteStartupApi } from '../api/home';
import '../styles/startup.scss';

function StartupConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'error' });
  const [config, setConfig] = useState(null); // 当前已保存的配置
  const [sourceType, setSourceType] = useState('file'); // file | url
  const [runMode, setRunMode] = useState('exehide');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState({ visible: false, title: '', message: '', onConfirm: null });
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ visible: true, message, type });
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await getStartupApi();
      const data = res.data;
      setConfig(data);
      if (data) {
        setSourceType(data.source_type || 'file');
        setRunMode(data.run_mode || 'exehide');
        if (data.source_type === 'url') setUrlInput(data.run_url || '');
      }
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    e.target.value = '';
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['exe', 'dll'].includes(ext)) { showToast('仅支持 EXE 或 DLL 格式', 'error'); return; }
    if (f.size > 10 * 1024 * 1024) { showToast('文件不能超过10MB', 'error'); return; }
    setFile(f);
    setSourceType('file');
    setUrlInput('');
  };

  const handleSave = async () => {
    if (!runMode) { showToast('请选择执行方式', 'error'); return; }
    if (sourceType === 'file' && !file && !config?.run_url) { showToast('请上传执行文件或输入URL', 'error'); return; }
    if (sourceType === 'url' && !urlInput.trim()) { showToast('请输入URL', 'error'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('run_mode', runMode);
      fd.append('source_type', sourceType);
      if (sourceType === 'file') {
        if (file) fd.append('startup_file', file);
      } else {
        fd.append('run_url', urlInput.trim());
      }
      await saveStartupApi(fd);
      showToast('保存成功', 'success');
      setFile(null);
      await loadConfig();
    } catch (err) { showToast(err.message || '保存失败', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = () => {
    setConfirm({
      visible: true,
      title: '删除开机启动项',
      message: '确定要删除当前的开机启动项配置吗？删除后文件将被清理。',
      onConfirm: async () => {
        setConfirm(p => ({ ...p, visible: false }));
        try {
          await deleteStartupApi();
          showToast('已删除', 'success');
          setConfig(null);
          setFile(null);
          setUrlInput('');
          setSourceType('file');
          setRunMode('exehide');
        } catch (err) { showToast(err.message || '删除失败', 'error'); }
      }
    });
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  if (loading) {
    return <DashboardLayout><Loading text="加载中..." /></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-header-title">开机启动项</h1>
        <p className="page-header-desc">配置客户端开机时自动执行的程序。</p>
      </div>

      <div className="startup-config-card">
        <div className="startup-section">
          <h3 className="startup-section-title"><Play size={16} /> 执行方式 <span className="required">*</span></h3>
          <div className="startup-dropdown" ref={dropdownRef}>
            <div className="startup-dropdown-trigger" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <span>{runMode}</span>
              <ChevronDown size={16} className={`startup-dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
            </div>
            {dropdownOpen && (
              <div className="startup-dropdown-menu">
                <div className={`startup-dropdown-item ${runMode === 'exehide' ? 'active' : ''}`} onClick={() => { setRunMode('exehide'); setDropdownOpen(false); }}>exehide</div>
                <div className={`startup-dropdown-item ${runMode === 'dll' ? 'active' : ''}`} onClick={() => { setRunMode('dll'); setDropdownOpen(false); }}>dll</div>
              </div>
            )}
          </div>
        </div>

        <div className="startup-section">
          <h3 className="startup-section-title"><Upload size={16} /> 执行文件 <span className="required">*</span></h3>
          <p className="startup-hint">支持 EXE 或 DLL 格式，最大10MB。上传文件或输入URL（二选一）。</p>

          <div className="startup-source-tabs">
            <button className={`startup-tab ${sourceType === 'file' ? 'active' : ''}`} onClick={() => { setSourceType('file'); setUrlInput(''); }}>上传文件</button>
            <button className={`startup-tab ${sourceType === 'url' ? 'active' : ''}`} onClick={() => { setSourceType('url'); setFile(null); }}>输入URL</button>
          </div>

          {sourceType === 'file' && (
            <div className="startup-file-area">
              <div className="startup-upload-btn" onClick={() => fileInputRef.current?.click()}>
                <Upload size={18} />
                <span>{file ? file.name : '点击选择文件'}</span>
              </div>
              <input ref={fileInputRef} type="file" accept=".exe,.dll" style={{ display: 'none' }} onChange={handleFileChange} />
              {file && (
                <button className="startup-remove-btn" onClick={handleRemoveFile}><X size={14} /> 移除</button>
              )}
              {!file && config && config.source_type === 'file' && config.file_name && (
                <div className="startup-current-file">
                  <span>当前文件: {config.file_name}</span>
                </div>
              )}
            </div>
          )}

          {sourceType === 'url' && (
            <div className="startup-url-area">
              <div className="startup-url-input-wrap">
                <Link size={16} />
                <input
                  type="text"
                  className="startup-url-input"
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  placeholder="输入执行文件URL地址"
                />
              </div>
              {config && config.source_type === 'url' && config.run_url && !urlInput && (
                <div className="startup-current-file">
                  <span>当前URL: {config.run_url}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="startup-actions">
          <button className="startup-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
          {config && (
            <button className="startup-btn danger" onClick={handleDelete}>
              <Trash2 size={14} /> 删除配置
            </button>
          )}
        </div>
      </div>

      <Toast visible={toast.visible} message={toast.message} type={toast.type} onClose={() => setToast({ visible: false, message: '', type: 'error' })} />
      <ConfirmModal visible={confirm.visible} title={confirm.title} message={confirm.message} danger
        confirmText="确认删除" onConfirm={confirm.onConfirm} onCancel={() => setConfirm(p => ({ ...p, visible: false }))} />
    </DashboardLayout>
  );
}

export default StartupConfig;
